import { api } from './client'
import type { ApiEnvelope, PaginatedResponse } from '@/types/api'

interface SpringPage<T> {
    content: T[]
    totalElements: number
    totalPages: number
    number: number
    size: number
}

interface RobotEventApiItem {
    id?: unknown
    robotEventId?: unknown
    eventId?: unknown
    name?: unknown
    eventName?: unknown
    startDate?: unknown
    endDate?: unknown
    startTime?: unknown
    endTime?: unknown
    repeat?: unknown
    isRepeat?: unknown
    loop?: unknown
    numberOfPlays?: unknown
    eventType?: unknown
    type?: unknown
    status?: unknown
    active?: unknown
    allowRepeat?: unknown
    repeatType?: unknown
    repeatValue?: unknown
    priority?: unknown
    eventInfo?: {
        id?: unknown
        name?: unknown
        status?: unknown
        startDate?: unknown
        endDate?: unknown
        startTime?: unknown
        endTime?: unknown
    } | null
}

export interface RobotEventListItem {
    id: string
    eventId: string
    eventName: string
    name: string
    startDate?: string
    endDate?: string
    startTime?: string
    endTime?: string
    repeatType?: number
    repeatValue?: string
    allowRepeat: number
    eventType: 'Khẩn cấp' | 'Lập lịch'
    active: number
    status: boolean
    priority?: number
}

export interface RobotEventDetailItem extends RobotEventListItem {
    eventTypeValue: number
    startTime?: string
    endTime?: string
}

export interface RobotEventQueryParams {
    page?: number
    size?: number
    keySearch?: string
    status?: number
    eventType?: number
    startDateFrom?: string
    startDateTo?: string
}

export interface CreateRobotEventPayload {
    eventId: string
    eventType: number
    startDate?: string
    endDate?: string
    active?: number
    startTime?: string
    endTime?: string
}

export interface UpdateRobotEventPayload extends CreateRobotEventPayload {
    priority?: number
}

function asString(value: unknown): string {
    if (typeof value === 'string') return value
    if (typeof value === 'number') return String(value)
    return ''
}

function asNumber(value: unknown): number | undefined {
    if (typeof value === 'number' && !Number.isNaN(value)) return value
    if (typeof value === 'string' && value.trim() !== '') {
        const parsed = Number(value)
        return Number.isNaN(parsed) ? undefined : parsed
    }
    return undefined
}

function asBoolean(value: unknown): boolean {
    if (typeof value === 'boolean') return value
    if (typeof value === 'number') return value === 1
    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase()
        return normalized === '1' || normalized === 'true' || normalized === 'active'
    }
    return false
}


function toEventTypeLabel(value: unknown): 'Khẩn cấp' | 'Lập lịch' {
    const numeric = asNumber(value)
    if (numeric === 1) return 'Khẩn cấp'
    if (numeric === 2) return 'Lập lịch'

    const normalized = asString(value).trim().toLowerCase()
    if (
        normalized.includes('khẩn') ||
        normalized.includes('khan') ||
        normalized === 'urgent' ||
        normalized === 'emergency'
    ) {
        return 'Khẩn cấp'
    }

    return 'Lập lịch'
}

function normalizeRobotEvent(item: RobotEventApiItem): RobotEventListItem {
    const nestedEventInfo = item.eventInfo
    const eventTypeNumber = asNumber(item.eventType ?? item.type)
    const repeatValue = item.allowRepeat

    return {
        id: asString(item.robotEventId ?? item.id ?? item.eventId),
        eventId: asString(item.eventId ?? nestedEventInfo?.id ?? item.robotEventId ?? item.id),
        eventName: asString(item.eventName ?? item.name ?? nestedEventInfo?.name),
        name: asString(item.name ?? item.eventName ?? nestedEventInfo?.name) || '',
        startDate: item.startDate ? asString(item.startDate ?? nestedEventInfo?.startDate) : undefined,
        endDate: item.endDate ? asString(item.endDate ?? nestedEventInfo?.endDate) : undefined,
        startTime: item.startTime ? asString(item.startTime ?? nestedEventInfo?.startTime) : undefined,
        endTime: item.endTime ? asString(item.endTime ?? nestedEventInfo?.endTime) : undefined,
        allowRepeat: asNumber(repeatValue) ?? 0,
        repeatType: asNumber(item.repeatType) ?? 1,
        repeatValue: asString(item.repeatValue) || undefined,
        eventType: toEventTypeLabel(eventTypeNumber ?? item.eventType ?? item.type),
        status: asBoolean(item.status ?? nestedEventInfo?.status),
        active: Number(item.active ?? nestedEventInfo?.status),
        priority: asNumber(item.priority),
    }
}

function normalizeRobotEventDetail(item: RobotEventApiItem): RobotEventDetailItem {
    const normalized = normalizeRobotEvent(item)
    const eventTypeNumber = asNumber(item.eventType ?? item.type) ?? 2
    const allowRepeatNumber =
        asNumber(item.allowRepeat ?? item.repeat ?? item.isRepeat ?? item.loop) ?? 0
    const repeatTypeNumber = asNumber(item.repeatType) ?? 1

    return {
        ...normalized,
        eventTypeValue: eventTypeNumber,
        allowRepeat: allowRepeatNumber,
        repeatType: repeatTypeNumber,
        repeatValue: asString(item.repeatValue) || undefined,
        startTime: asString(item.startTime) || undefined,
        endTime: asString(item.endTime) || undefined,
    }
}

function adaptPage<T>(
    raw: SpringPage<T> | PaginatedResponse<T> | T[] | null | undefined,
): PaginatedResponse<T> {
    if (!raw) {
        return { items: [], total: 0, page: 1, limit: 10, totalPages: 0 }
    }

    if (Array.isArray(raw)) {
        return {
            items: raw,
            total: raw.length,
            page: 1,
            limit: Math.max(10, raw.length),
            totalPages: 1,
        }
    }

    if ('items' in raw) {
        return {
            ...raw,
            items: raw.items,
        }
    }

    return {
        items: raw.content,
        total: raw.totalElements || 0,
        page: (raw.number || 0) + 1,
        limit: raw.size || 10,
        totalPages: raw.totalPages || 0,
    }
}

export async function fetchRobotEvents(
    robotId: string,
    params: RobotEventQueryParams = {},
    signal?: AbortSignal,
): Promise<PaginatedResponse<RobotEventListItem>> {
    try {

        const response = await api.get<
            ApiEnvelope<
                SpringPage<RobotEventApiItem> | PaginatedResponse<RobotEventApiItem> | RobotEventApiItem[]
            >
        >(`/v1/robot/config/event/${robotId}/search`, {
            params: {
                page: params.page ?? 0,
                size: params.size ?? 10,
                keySearch: params.keySearch || undefined,
                status: params.status,
                eventType: params.eventType,
                startDateFrom: params.startDateFrom,
                startDateTo: params.startDateTo,
            },
            signal,
        })

        const adapted = adaptPage(response.data.responseData)

        return {
            ...adapted,
            items: adapted.items.map((item) => normalizeRobotEvent(item)),
        }
    } catch (error: any) {
        const message =
            error?.response?.data?.responseMessage ||
            error?.message ||
            'Đã xảy ra lỗi khi lấy danh sách sự kiện của robot.'
        throw new Error(message)
    }
}

export async function createRobotEvent(
    robotId: string,
    payload: CreateRobotEventPayload,
): Promise<unknown> {
    try {
        const response = await api.post(
            `/v1/robot/config/event/${robotId}`,
            payload,
        )
        return response.data
    } catch (error: any) {
        const message =
            error?.response?.data?.responseMessage ||
            error?.message ||
            'Đã xảy ra lỗi khi thêm sự kiện cho robot.'
        throw new Error(message)
    }
}

export async function updateRobotEvent(
    robotId: string,
    eventId: string,
    payload: UpdateRobotEventPayload,
): Promise<unknown> {
    try {
        const response = await api.put(
            `/v1/robot/config/event/${robotId}/${eventId}`,
            payload,
        )
        return response.data
    } catch (error: any) {
        const message =
            error?.response?.data?.responseMessage ||
            error?.message ||
            'Đã xảy ra lỗi khi cập nhật sự kiện cho robot.'
        throw new Error(message)
    }
}

export async function fetchRobotEventDetail(
    robotId: string,
    robotEventId: string,
    signal?: AbortSignal,
): Promise<RobotEventDetailItem> {
    try {
        const response = await api.get<ApiEnvelope<RobotEventApiItem>>(
            `/v1/robot/config/event/${robotId}/${robotEventId}`,
            { signal },
        )

        return normalizeRobotEventDetail(response.data.responseData)
    } catch (error: any) {
        const message =
            error?.response?.data?.responseMessage ||
            error?.message ||
            'Đã xảy ra lỗi khi lấy chi tiết sự kiện của robot.'
        throw new Error(message)
    }
}

export async function deleteRobotEvent(
    robotId: string,
    robotEventId: string,
): Promise<unknown> {
    try {
        const response = await api.delete(
            `/v1/robot/config/event/${robotId}/${robotEventId}`,
        )
        return response.data
    } catch (error: any) {
        const message =
            error?.response?.data?.responseMessage ||
            error?.message ||
            'Đã xảy ra lỗi khi xóa sự kiện cho robot.'
        throw new Error(message)
    }
}


